import type { APIClient } from "@heidelbergmaterials/fcl-apiclient"
import {
	ControlledSwitch,
	spawnErrorToast,
	spawnSuccessToast,
	TextRegular,
} from "@hm/fundamental"
import type { NotificationPreferenceResponse } from "@shared/api"
import {
	NotificationCategory,
	getNotificationPreferences,
	updateNotificationPreference,
} from "@shared/api"
import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

const DEFAULT_PREFERENCES: NotificationPreferenceResponse[] = [
	{ category: NotificationCategory.InvoicingInformation, isEnabled: true },
	{
		category: NotificationCategory.DeclarationStatusUpdates,
		isEnabled: true,
	},
]

interface NotificationSettingsProps {
	apiClient: APIClient
}

export const NotificationSettings: React.FC<NotificationSettingsProps> = ({
	apiClient,
}) => {
	const { t } = useTranslation("features", {
		keyPrefix: "userMenu.notificationSettings",
	})

	const [preferences, setPreferences] =
		useState<NotificationPreferenceResponse[]>(DEFAULT_PREFERENCES)

	useEffect(() => {
		const controller = new AbortController()

		const fetchPreferences = async () => {
			try {
				const data = await getNotificationPreferences(
					apiClient,
					controller.signal,
				)
				setPreferences(data)
			} catch {
				// Fall back to defaults silently — preferences default to enabled
			}
		}

		fetchPreferences()

		return () => {
			controller.abort()
		}
	}, [apiClient])

	const handleToggle = useCallback(
		async (category: NotificationCategory, newValue: string) => {
			const isEnabled = newValue === "enabled"

			setPreferences(prev =>
				prev.map(p =>
					p.category === category ? { ...p, isEnabled } : p,
				),
			)

			try {
				await updateNotificationPreference(apiClient, {
					category,
					isEnabled,
				})
				spawnSuccessToast(t("toast.success"))
			} catch {
				setPreferences(prev =>
					prev.map(p =>
						p.category === category
							? { ...p, isEnabled: !isEnabled }
							: p,
					),
				)
				spawnErrorToast(t("toast.error"))
			}
		},
		[apiClient, t],
	)

	const isEnabled = (category: NotificationCategory) =>
		preferences.find(p => p.category === category)?.isEnabled !== false

	const switchOptions = [
		{ label: t("enabled"), value: "enabled" },
		{ label: t("disabled"), value: "disabled" },
	]

	return (
		<div
			className="flex flex-col px-4 py-3"
			data-cy="notification-settings-panel"
		>
			<div
				className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 border-b border-border py-3"
				data-cy="notification-settings-invoicing"
			>
				<TextRegular className="min-w-0 text-[15px] leading-5">
					{t("invoicingInformation")}
				</TextRegular>
				<div className="shrink-0 origin-right scale-[0.75]">
					<ControlledSwitch
						data-cy="notification-toggle-invoicing"
						value={
							isEnabled(NotificationCategory.InvoicingInformation)
								? "enabled"
								: "disabled"
						}
						options={switchOptions}
						onChange={val =>
							handleToggle(
								NotificationCategory.InvoicingInformation,
								val as string,
							)
						}
					/>
				</div>
			</div>
			<div
				className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 border-b border-border py-3"
				data-cy="notification-settings-declaration"
			>
				<TextRegular className="min-w-0 text-[15px] leading-5">
					{t("declarationProcessingStatus")}
				</TextRegular>
				<div className="shrink-0 origin-right scale-[0.75]">
					<ControlledSwitch
						data-cy="notification-toggle-declaration"
						value={
							isEnabled(
								NotificationCategory.DeclarationStatusUpdates,
							)
								? "enabled"
								: "disabled"
						}
						options={switchOptions}
						onChange={val =>
							handleToggle(
								NotificationCategory.DeclarationStatusUpdates,
								val as string,
							)
						}
					/>
				</div>
			</div>
		</div>
	)
}
